//#region IMPORT
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Global from '../../../../Services/Common/Global';
import Commons from '../../../../Services/Common/Common';
import M_EduVideos_BASIC from './M_EduVideos_BASIC';
import { TbmActions } from '../../../../Reducer/TbmReducer';
import { useTbmSessionSave } from '../hook/useSaveTbmSession';
import { CommonActions } from '../../../../Reducer/CommonReducer';
import {
  GetPlayedFractionFromPlayer,
  useHostVideoWebSocketSync,
  useParticipantVideoSeekSync,
} from '../hook/useTbmEduVideoSync';
import { useBackStack } from '../../../../Hooks/back/useBackStack';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { usePdfStepTransition } from '../hook/usePdfStepTransition';
import { shouldOpenMeetingDrawerOnNext } from '../hook/tbmStepFlow';
//#endregion

/** 세션 상대 경로 → 스트리밍용 전체 URL */
function BuildVideoUrl(relativePath) {
  if (!relativePath) return '';
  return `${Global.file.useUrl}files/${Commons.EncodeUrl(relativePath)}`;
}

function M_EduVideos_BASICF(props) {
  //#region 변수
  const dispatch = useDispatch();
  const {
    sessionData,
    stepData,
    participantData,
    videoPlaying,
    videoPlayedFraction,
  } = useSelector((state) => state.tbm);
  const activeStep = useSelector((state) => state.common.activeStep);

  const isHost = participantData.hostYn === 'Y';
  const currentStep = useMemo(
    () => stepData.find((s) => s.stepCode === props.stepCode),
    [stepData, props.stepCode]
  );

  const isMountedRef = React.useRef(true);

  const CleanupForTransition = useCallback(() => {}, []);

  const { isTransitioning, startTransition } = usePdfStepTransition({
    cleanup: CleanupForTransition,
    isMountedRef,
  });
  const [inputs, setInputs] = useState({ video: '', isNext: false });
  const [played, setPlayed] = useState(0); // 진행자: onProgress로 갱신되는 재생 비율(0~1) — 세션 저장·자식에 전달
  const [transitionDirection, setTransitionDirection] = useState('next');

  /** ReactPlayer 인스턴스 ref */
  const videoRef = useRef();
  /** 최초 1회 lastPosition/동기화 fraction 적용 후 true — 이후부터 onProgress 반영 */
  const isInitialSeekDone = useRef(false);
  /** 참가자: API isPlaying 1회 반영 여부 */
  const participantPlayInitDone = useRef(false);
  /** 진행자: 마운트 직후 videoPlaying=false 일 때 세션저장·웹소켓 송신 생략 */
  // const hostSkipFirstVideoPlayingEmit = useRef(true);
  //#endregion

  const CleanupVideoStep = () => {
    try {
      const p = videoRef.current?.getInternalPlayer?.();
      if (p?.pause) p.pause();
    } catch (_) {}
    setInputs({ video: '', isNext: false });
    setPlayed(0);
    isInitialSeekDone.current = false;
    participantPlayInitDone.current = false;
    dispatch(TbmActions.setVideoPlaying(false));
  };

  //#region 커스텀훅
  useBackStack({
    type: 'custom',
    enabled: activeStep > 0,
    onBack: () => {
      if (!isHost) {
        dispatch(
          showSnackbar({
            message: 'TBM 진행중 뒤로가기가 불가능합니다.',
            type: 'warning',
          })
        );
        return;
      }
      setTransitionDirection('prev');
      props.ActiveButton({ type: 'stepChange', step: activeStep - 1 });
      dispatch(
        TbmActions.setStepTransition({
          isTransitioning: true,
          direction: 'prev',
        })
      );
      startTransition(() => {
        dispatch(
          TbmActions.setStepTransition({
            isTransitioning: false,
            direction: 'prev',
          })
        );
        dispatch(CommonActions.setActiveStep(activeStep - 1));
        CleanupVideoStep();
      });
    },
  });
  const { SaveTbmSession } = useTbmSessionSave();

  /** 진행자 전용: 웹소켓 동기화(디바운스 후 송신) */
  const { ScheduleEmitHostVideoSync } = useHostVideoWebSocketSync({
    isHost,
    videoRef,
    played,
    videoPlaying,
    ActiveButton: props.ActiveButton,
  });

  /** 참가자: Redux videoPlayedFraction(웹소켓 수신)에 맞춰 seek */
  useParticipantVideoSeekSync({ isHost, videoRef, videoPlayedFraction });
  //#endregion

  //#region 이펙트
  /** 영상 URL 세팅·없으면 다음 스텝 이동 */
  useEffect(() => {
    const path = sessionData.videoPath;
    if (path) {
      isInitialSeekDone.current = false;
      setInputs((prev) => ({ ...prev, video: BuildVideoUrl(path) }));
    }
  }, [dispatch, sessionData.videoPath, activeStep]);

  /** 진행자: 세션이 OPEN이면 재생 플래그 true */
  useEffect(() => {
    if (!isHost) return;
    if (sessionData.sessionStatus === 'OPEN') {
      dispatch(TbmActions.setVideoPlaying(true));
    }
  }, [sessionData.sessionStatus, isHost, dispatch]);

  /** 참가자: 세션에 저장된 isPlaying으로 Redux videoPlaying 초기화(1회) */
  useEffect(() => {
    if (isHost) return;
    if (participantPlayInitDone.current) return;
    if (!sessionData.sessionCd) return;
    if (sessionData.isPlaying === undefined || sessionData.isPlaying === null) {
      participantPlayInitDone.current = true;
      return;
    }
    participantPlayInitDone.current = true;
    dispatch(
      TbmActions.setVideoPlaying(
        sessionData.isPlaying === 'Y' || sessionData.isPlaying === true
      )
    );
  }, [sessionData.sessionCd, sessionData.isPlaying, isHost, dispatch]);

  /** 진행자: 재생/일시정지 변경 시 세션 저장 + 참가자에게 웹소켓 동기화 */
  useEffect(() => {
    if (!isHost) return;
    if (Object.keys(sessionData).length === 0) return;
    // if (hostSkipFirstVideoPlayingEmit.current) {
    //   hostSkipFirstVideoPlayingEmit.current = false;
    //   return;
    // }
    SaveTbmSession([
      {
        ...sessionData,
        curStepOrder: currentStep?.stepOrder,
        curStepCode: currentStep?.stepCode,
        curPosition: played || currentStep?.lastPosition,
        isPlaying: videoPlaying ? 'Y' : 'N',
      },
    ]);
    ScheduleEmitHostVideoSync();
    // 재생/일시정지 토글 시에만 실행; played·sessionData는 그 시점 렌더 값 사용
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoPlaying]);
  //#endregion

  //#region 나머지 함수
  /** 재생 시작 → Redux videoPlaying true */
  const HandlePlay = () => {
    dispatch(TbmActions.setVideoPlaying(true));
  };

  /** 일시정지 → Redux videoPlaying false */
  const HandlePause = () => {
    dispatch(TbmActions.setVideoPlaying(false));
  };

  /** 재생 끝 → 정지 + 다음 단계 버튼(isNext) 활성 */
  const HandleVideoEnd = () => {
    dispatch(TbmActions.setVideoPlaying(false));
    setInputs((prev) => ({ ...prev, isNext: true }));
  };

  /** 재생 진행률 갱신(최초 seek 이후에만 played 반영) */
  const HandleProgress = (state) => {
    if (isInitialSeekDone.current) {
      setPlayed(state.played);
    }
  };

  /** 메타데이터 준비 완료: lastPosition 또는 참가자는 videoPlayedFraction으로 1회 seek, 진행자는 동기화 예약 */
  const HandleReady = () => {
    if (isInitialSeekDone.current) return;

    const lastPosition = Number(currentStep?.lastPosition) || 0;
    const player = videoRef.current;
    if (player) {
      if (!isHost) {
        const vf = videoPlayedFraction;
        const target =
          typeof vf === 'number' && vf > 0
            ? vf
            : lastPosition > 0
              ? lastPosition
              : 0;
        if (target > 0) player.seekTo(target, 'fraction');
      } else if (lastPosition > 0) {
        player.seekTo(lastPosition, 'fraction');
      }
    }

    isInitialSeekDone.current = true;
    if (isHost) {
      ScheduleEmitHostVideoSync();
    }
  };

  /** 진행자 탐색바 이동 시 played 반영 + 웹소켓 위치 동기화(디바운스) */
  const HandleSeek = () => {
    if (!isHost || !videoRef.current) return;
    try {
      const fraction = GetPlayedFractionFromPlayer(videoRef);
      if (fraction == null) return;
      setPlayed(fraction);
      ScheduleEmitHostVideoSync();
    } catch (error) {
      console.log('Seek Error: ', error);
    }
  };

  /** 다음 단계: 세션 저장 후 전환 오버레이를 띄우며 stepChange 전송 */
  const advanceToNextStep = useCallback(async () => {
    if (isHost) {
      await SaveTbmSession([
        {
          ...sessionData,
          curStepOrder: currentStep?.stepOrder,
          curStepCode: currentStep?.stepCode,
          curPosition: played || currentStep?.lastPosition,
          isPlaying: videoPlaying ? 'Y' : 'N',
        },
      ]);
    }
    if (shouldOpenMeetingDrawerOnNext(stepData, props.stepCode)) {
      props.ActiveButton({ type: 'openMeetingDrawer' });
      return;
    }
    setTransitionDirection('next');
    props.ActiveButton({ type: 'stepChange', step: activeStep + 1 });
    dispatch(
      TbmActions.setStepTransition({ isTransitioning: true, direction: 'next' })
    );
    startTransition(() => {
      dispatch(
        TbmActions.setStepTransition({
          isTransitioning: false,
          direction: 'next',
        })
      );
      dispatch(CommonActions.setActiveStep(activeStep + 1));
    });
  }, [
    isHost,
    SaveTbmSession,
    sessionData,
    currentStep,
    played,
    videoPlaying,
    stepData,
    props.stepCode,
    startTransition,
    props.ActiveButton,
    activeStep,
  ]);

  /** 자동재생 거부 등은 재생 해제, 소스 오류 시 사용자 알림 후 영상 없음 처리 */
  const HandleVideoError = (e) => {
    console.log('Video Error: ', e);
    const msg =
      typeof e === 'string' ? e : (e?.message ?? e?.toString?.() ?? '');
    if (
      msg.includes('NotAllowedError') ||
      msg.includes("user didn't interact") ||
      msg.includes('play() request was interrupted')
    ) {
      if (isHost) {
        dispatch(TbmActions.setVideoPlaying(false));
      }
      return;
    }
    if (e?.type === 'error') {
      dispatch(
        showSnackbar({
          message:
            '동영상을 불러올 수 없습니다. 파일이 없거나 형식이 지원되지 않을 수 있습니다.',
          type: 'error',
        })
      );
      setInputs((prev) => ({ ...prev, video: '', isNext: true }));
    }
  };
  //#endregion

  return (
    <div className="wrap">
      <M_EduVideos_BASIC
        inputs={inputs}
        setInputs={setInputs}
        videoRef={videoRef}
        played={played}
        stepCode={props.stepCode}
        ActiveButton={props.ActiveButton}
        SaveTbmSession={SaveTbmSession}
        HandlePlay={HandlePlay}
        HandlePause={HandlePause}
        HandleVideoEnd={HandleVideoEnd}
        HandleProgress={HandleProgress}
        HandleReady={HandleReady}
        HandleSeek={HandleSeek}
        HandleVideoError={HandleVideoError}
        isTransitioning={isTransitioning}
        transitionDirection={transitionDirection}
        onAdvanceToNextStep={advanceToNextStep}
      />
    </div>
  );
}

export default M_EduVideos_BASICF;
